// ═══════════════════════════════════════════════════════════════
// DEBRIS — FEMA PA Forms Generator
// Forms verified against official FEMA FF-104-FY-21-xxx series:
//   009-0-49   Summary Record for Emergency Work (cover sheet)
//   009-0-123  Force Account Labor Summary Record       (FF-104-FY-21-137)
//   009-0-124  Materials Summary Record                 (FF-104-FY-21-138)
//   009-0-125  Rented Equipment Summary Record          (FF-104-FY-21-139)
//   009-0-126  Contract Work Summary Record             (FF-104-FY-21-140)
//   009-0-127  Force Account Equipment Summary Record   (FF-104-FY-21-141)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid, Select,
} from '../../components/gab/GabComponents';
import { IconFileTypePdf, IconDownload } from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useAuthStore } from '../../store/authStore';
import { fetchRecords, queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

// ── Helpers ──────────────────────────────────────────────────
const fmt$ = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => {
  if (!d) return '';
  const s = d.slice(0, 10);
  if (s.includes('-')) { const [y, m, day] = s.split('-'); return `${m}/${day}/${y}`; }
  return s;
};

// ── Standard FEMA header — matches FF-104-FY-21-xxx grid layout ─
interface FemaHeader {
  applicant: string;
  paId: string;
  disasterNumber: string;
  projectNumber: string;
  periodStart: string;
  periodEnd: string;
  location: string;
  category: string;
  descriptionOfWork: string;
}

function addFemaHeader(doc: jsPDF, formNumber: string, formTitle: string, h: FemaHeader) {
  const pw = doc.internal.pageSize.getWidth();
  const L = 14, R = pw - 14, W = R - L;

  // Top rule
  doc.setDrawColor(0); doc.setLineWidth(1.5);
  doc.line(L, 10, R, 10);

  // DHS / FEMA branding line
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
  doc.text('DEPARTMENT OF HOMELAND SECURITY / FEDERAL EMERGENCY MANAGEMENT AGENCY', L, 15);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal');
  doc.text(`OMB No. 1660-0017  |  FEMA Form ${formNumber}`, R, 15, { align: 'right' });

  // Form title
  doc.setFontSize(12); doc.setFont('helvetica', 'bold');
  doc.text(formTitle.toUpperCase(), pw / 2, 21, { align: 'center' });

  // Header cells
  doc.setLineWidth(0.3);
  let y = 24;
  const rowH = 7;
  const c1 = W * 0.44, c2 = W * 0.18, c3 = W - c1 - c2;

  const cell = (x: number, cy: number, w: number, ht: number, label: string, value: string) => {
    doc.rect(x, cy, w, ht);
    doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
    doc.text(label, x + 1, cy + 2.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.text(doc.splitTextToSize(value || '', w - 2), x + 1, cy + 5.5);
  };

  // Row 1: APPLICANT | PA ID NO. | DISASTER NO.
  cell(L,           y, c1, rowH, 'APPLICANT',    h.applicant);
  cell(L + c1,      y, c2, rowH, 'PA ID NO.',    h.paId);
  cell(L + c1 + c2, y, c3, rowH, 'DISASTER NO.', h.disasterNumber);
  y += rowH;

  // Row 2: LOCATION/SITE | PROJECT NO. | CATEGORY
  cell(L,           y, c1, rowH, 'LOCATION/SITE', h.location);
  cell(L + c1,      y, c2, rowH, 'PROJECT NO.',   h.projectNumber);
  cell(L + c1 + c2, y, c3, rowH, 'CATEGORY',      h.category);
  y += rowH;

  // Row 3: PERIOD COVERING (full width)
  const periodStr = h.periodStart ? `${fmtDate(h.periodStart)} to ${fmtDate(h.periodEnd)}` : '';
  cell(L, y, W, rowH, 'PERIOD COVERING', periodStr);
  y += rowH;

  // Row 4: DESCRIPTION OF WORK PERFORMED
  const descH = 11;
  doc.rect(L, y, W, descH);
  doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
  doc.text('DESCRIPTION OF WORK PERFORMED', L + 1, y + 2.5);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  if (h.descriptionOfWork) {
    doc.text(doc.splitTextToSize(h.descriptionOfWork, W - 2), L + 1, y + 6);
  }
  y += descH;

  doc.setLineWidth(0.5);
  doc.line(L, y, R, y);
  return y + 3; // starting Y for content
}

// ── Standard certification footer ────────────────────────────
function addFemaFooter(doc: jsPDF, y: number) {
  const pw = doc.internal.pageSize.getWidth();
  const L = 14, R = pw - 14, W = R - L;
  y += 6;
  doc.setLineWidth(0.5);
  doc.line(L, y, R, y);
  y += 5;

  // Certification statement
  doc.setFontSize(7); doc.setFont('helvetica', 'bold');
  doc.text(
    'I CERTIFY THAT THE ABOVE INFORMATION IS TRUE AND CORRECT AND THAT ALL COSTS ARE SUPPORTED BY PAYROLL RECORDS, INVOICES, OR OTHER DOCUMENTS AVAILABLE FOR AUDIT.',
    pw / 2, y, { align: 'center', maxWidth: W }
  );
  y += 8;

  // Signature blocks
  doc.setLineWidth(0.3);
  const bw = W / 3;
  ['CERTIFIED (Signature)', 'TITLE', 'DATE'].forEach((label, i) => {
    const bx = L + i * bw;
    doc.rect(bx, y, bw, 9);
    doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
    doc.text(label, bx + 1, y + 2.5);
  });
  y += 15;

  doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
  doc.text('Generated by OpenGov Emergency Management Console', pw / 2, y, { align: 'center' });
  doc.setTextColor(0);
}

// ── COMPONENT ────────────────────────────────────────────────
export default function DebrisFemaForms() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const { contractors, trucks, loadContractors, loadTrucks } = useDebrisStore();
  const { invoices: allInvoices, loadInvoices } = useDebrisOpsStore();
  const { reconciliations: allReconciliations, femaPackages: allFemaPackages, loadReconciliations, loadFemaPackages } = useTampaDebrisStore();

  // Client-side incident scoping — stores may return stale cross-incident data
  const invoices = useMemo(() =>
    incidentId ? allInvoices.filter(i => String(i.incidentId) === incidentId) : [],
  [allInvoices, incidentId]);
  const reconciliations = useMemo(() =>
    incidentId ? allReconciliations.filter(r => String(r.incidentId) === incidentId) : [],
  [allReconciliations, incidentId]);
  const femaPackages = useMemo(() =>
    incidentId ? allFemaPackages.filter(p => String(p.incidentId) === incidentId) : [],
  [allFemaPackages, incidentId]);

  // Force account data
  const [faLabor, setFaLabor] = useState<any[]>([]);
  const [faEquipment, setFaEquipment] = useState<any[]>([]);
  const [rentedEquip, setRentedEquip] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Personnel roster fringe rate lookup: employee name (lowercase) → fringe rate %
  const [rosterFringeMap, setRosterFringeMap] = useState<Map<string, number>>(new Map());

  // Config fields
  const [disasterNumber, setDisasterNumber] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [paId, setPaId] = useState('');
  const [applicantName] = useState('City of Tampa, FL');
  const [location] = useState('Tampa, Hillsborough County, FL');

  useEffect(() => {
    loadContractors(); loadTrucks(); loadInvoices(); loadReconciliations(); loadFemaPackages();
  }, [loadContractors, loadTrucks, loadInvoices, loadReconciliations, loadFemaPackages]);

  // Load force account data + personnel roster fringe rates
  useEffect(() => {
    if (!incidentId) return;
    setLoading(true);
    const FL = FIELDS.debrisForceAccountLabor;
    const FE = FIELDS.debrisForceAccountEquipment;
    const FR = FIELDS.paRentedEquipment;
    const FDoc = FIELDS.debrisDocuments;
    Promise.all([
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, {
        limit: 500,
        filters: [{ field: FL.incidentId, operator: 'eq' as const, value: incidentId }],
      }).then(res =>
        res.records.filter(r => String(r[FL.incidentId] ?? '') === incidentId).map(r => ({
          employeeName: String(r[FL.employeeName] ?? ''),
          employeeId: String(r[FL.employeeId] ?? ''),
          classification: String(r[FL.classification] ?? ''),
          dateWorked: String(r[FL.dateWorked] ?? ''),
          hoursRegular: Number(r[FL.hoursRegular]) || 0,
          hoursOvertime: Number(r[FL.hoursOvertime]) || 0,
          hourlyRate: Number(r[FL.hourlyRate]) || 0,
          totalCost: Number(r[FL.totalCost]) || 0,
        }))
      ).catch(() => []),
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, {
        limit: 500,
        filters: [{ field: FE.incidentId, operator: 'eq' as const, value: incidentId }],
      }).then(res =>
        res.records.filter(r => String(r[FE.incidentId] ?? '') === incidentId).map(r => ({
          fleetNumber: String(r[FE.fleetNumber] ?? ''),
          equipmentType: String(r[FE.equipmentType] ?? ''),
          description: String(r[FE.description] ?? ''),
          dateUsed: String(r[FE.dateUsed] ?? ''),
          hoursUsed: Number(r[FE.hoursUsed]) || 0,
          hourlyRate: Number(r[FE.hourlyRate]) || 0,
          totalCost: Number(r[FE.totalCost]) || 0,
          operatorName: String(r[FE.operatorName] ?? ''),
        }))
      ).catch(() => []),
      // paRentedEquipment has no incidentId — filter by projectId linked to this incident's PA projects
      fetchRecords<Record<string, unknown>>(TABLES.paRentedEquipment, { limit: 500 }).then(res =>
        res.records.map(r => ({
          equipmentName: String(r[FR.equipmentName] ?? r[FR.equipmentType] ?? ''),
          vendor: String(r[FR.vendor] ?? ''),
          datesUsed: String(r[FR.rentalPeriod] ?? ''),
          rateWithOpr: '',
          rateWithoutOpr: String(r[FR.rentalRate] ?? r[FR.rate] ?? ''),
          totalCost: String(r[FR.totalCost] ?? ''),
          dateAmountPaid: '',
          invoiceNumber: String(r[FR.rentalAgreementNumber] ?? ''),
          checkNumber: '',
        }))
      ).catch(() => []),
      // Load personnel roster to get fringe rates — filtered by incident
      queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
        limit: 50,
        filters: [{ field: FDoc.incidentId, operator: 'eq' as const, value: incidentId }],
      }).then(res => {
        const doc = res.records.find(r => r[FDoc.documentType] === 'roster_data');
        if (!doc) return new Map<string, number>();
        try {
          const people = JSON.parse(String(doc[FDoc.notes] || '[]')) as { name: string; employeeId?: string; fringeRate?: number }[];
          const map = new Map<string, number>();
          people.forEach(p => {
            if (p.fringeRate) {
              map.set(p.name.toLowerCase().trim(), p.fringeRate);
              if (p.employeeId) map.set(`id:${p.employeeId}`, p.fringeRate);
            }
          });
          return map;
        } catch { return new Map<string, number>(); }
      }).catch(() => new Map<string, number>()),
    ]).then(([labor, equip, rented, fringeMap]) => {
      setFaLabor(labor);
      setFaEquipment(equip);
      // paRentedEquipment has no incidentId — only show if this incident has FA activity
      setRentedEquip(labor.length > 0 || equip.length > 0 ? rented : []);
      setRosterFringeMap(fringeMap as Map<string, number>);
    }).finally(() => setLoading(false));
  }, [incidentId]);

  // Period from data
  const period = useMemo(() => {
    const dates: string[] = [];
    faLabor.forEach(l => { if (l.dateWorked) dates.push(l.dateWorked); });
    faEquipment.forEach(e => { if (e.dateUsed) dates.push(e.dateUsed); });
    invoices.forEach(i => { if (i.periodStart) dates.push(i.periodStart); if (i.periodEnd) dates.push(i.periodEnd); });
    if (dates.length === 0) return { start: '', end: '' };
    dates.sort();
    return { start: dates[0], end: dates[dates.length - 1] };
  }, [faLabor, faEquipment, invoices]);

  const workDescription = `${activeIncident?.name || 'Hurricane'} debris removal operations — right-of-way clearance, vegetative and C&D debris collection, transport, and disposal.`;

  const headerData: FemaHeader = {
    applicant: applicantName,
    paId,
    disasterNumber,
    projectNumber: projectNumber || 'TBD',
    periodStart: period.start,
    periodEnd: period.end,
    location,
    category: 'A — Debris Removal',
    descriptionOfWork: workDescription,
  };

  // Computed totals — labor total includes fringe from roster
  const laborTotal = useMemo(() => faLabor.reduce((s, l) => {
    const regPay = l.hoursRegular * l.hourlyRate;
    const otPay = l.hoursOvertime * (l.hourlyRate * 1.5);
    const benRate =
      rosterFringeMap.get(l.employeeName.toLowerCase().trim()) ??
      (l.employeeId ? rosterFringeMap.get(`id:${l.employeeId}`) : undefined) ??
      0;
    const fringe = (l.hoursRegular + l.hoursOvertime) * l.hourlyRate * (benRate / 100);
    return s + regPay + otPay + fringe;
  }, 0), [faLabor, rosterFringeMap]);
  const equipTotal = faEquipment.reduce((s, e) => s + e.totalCost, 0);
  const contractTotal = useMemo(() => {
    const approved = reconciliations.filter(r => r.status === 'approved');
    if (approved.length > 0) return approved.reduce((s, r) => s + (r.approvedAmount || 0), 0);
    return invoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
  }, [reconciliations, invoices]);
  const rentedTotal = rentedEquip.reduce((s, r) => s + (parseFloat(r.totalCost) || 0), 0);
  const grandTotal = laborTotal + equipTotal + contractTotal + rentedTotal;

  // ── PDF Generators ─────────────────────────────────────────

  // 009-0-49 Summary Record
  const generate49 = () => {
    const doc = new jsPDF();
    const y = addFemaHeader(doc, '009-0-49', 'Summary Record for Emergency Work', headerData);

    autoTable(doc, {
      startY: y,
      head: [['Cost Category', 'FEMA Form No.', 'Amount']],
      body: [
        ['Force Account Labor',      '009-0-123 (FF-104-FY-21-137)', fmt$(laborTotal)],
        ['Force Account Equipment',  '009-0-127 (FF-104-FY-21-141)', fmt$(equipTotal)],
        ['Contract Work',            '009-0-126 (FF-104-FY-21-140)', fmt$(contractTotal)],
        ['Materials',                '009-0-124 (FF-104-FY-21-138)', '$0.00'],
        ['Rented Equipment',         '009-0-125 (FF-104-FY-21-139)', fmt$(rentedEquip.reduce((s, r) => s + (parseFloat(r.totalCost) || 0), 0))],
      ],
      foot: [['', 'GRAND TOTAL', fmt$(grandTotal)]],
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold' },
      footStyles: { fillColor: [240, 245, 250], fontStyle: 'bold', textColor: [0, 0, 0] },
      columnStyles: { 2: { halign: 'right', fontStyle: 'bold' } },
      margin: { left: 14, right: 14 },
    });

    addFemaFooter(doc, (doc as any).lastAutoTable.finalY);
    doc.save(`FEMA-009-0-49-Summary-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-49 generated', color: 'var(--color-success)' });
  };

  // 009-0-123 Force Account Labor Summary Record (FF-104-FY-21-137)
  // Columns per FEMA form: Name | Job Title | Date(s) | REG Hrs | O.T. Hrs | Hourly Rate | Benefit Rate % | Benefit Rate/Hr | Regular Pay | Overtime Pay | Total Cost
  const generate123 = () => {
    const doc = new jsPDF('landscape');
    const y = addFemaHeader(doc, '009-0-123 (FF-104-FY-21-137)', 'Force Account Labor Summary Record', headerData);

    autoTable(doc, {
      startY: y,
      head: [['Name', 'Job Title', 'Date(s) Worked', 'REG Hrs', 'O.T. Hrs', 'Hourly Rate', 'Benefit Rate %', 'Benefit Rate/Hr', 'Regular Pay', 'Overtime Pay', 'Total Cost']],
      body: faLabor.map(l => {
        const regPay = l.hoursRegular * l.hourlyRate;
        const otPay = l.hoursOvertime * (l.hourlyRate * 1.5);
        // Look up fringe rate: try name first, then employee ID
        const benRate =
          rosterFringeMap.get(l.employeeName.toLowerCase().trim()) ??
          (l.employeeId ? rosterFringeMap.get(`id:${l.employeeId}`) : undefined) ??
          0;
        const benPerHr = l.hourlyRate * (benRate / 100);
        const totalHours = l.hoursRegular + l.hoursOvertime;
        const total = regPay + otPay + totalHours * benPerHr;
        return [
          l.employeeName,
          l.classification,
          fmtDate(l.dateWorked),
          l.hoursRegular.toFixed(1),
          l.hoursOvertime.toFixed(1),
          fmt$(l.hourlyRate),
          benRate ? `${benRate}%` : '—',
          benPerHr ? fmt$(benPerHr) : '—',
          fmt$(regPay),
          fmt$(otPay),
          fmt$(total),
        ];
      }),
      foot: [[
        'TOTALS', '', '',
        faLabor.reduce((s, l) => s + l.hoursRegular, 0).toFixed(1),
        faLabor.reduce((s, l) => s + l.hoursOvertime, 0).toFixed(1),
        '', '', '',
        fmt$(faLabor.reduce((s, l) => s + l.hoursRegular * l.hourlyRate, 0)),
        fmt$(faLabor.reduce((s, l) => s + l.hoursOvertime * l.hourlyRate * 1.5, 0)),
        fmt$(laborTotal), // includes fringe
      ]],
      styles: { fontSize: 6.5, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold', fontSize: 6 },
      footStyles: { fillColor: [240, 245, 250], fontStyle: 'bold', textColor: [0, 0, 0] },
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'right' },
        5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
        8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' },
      },
      margin: { left: 10, right: 10 },
    });

    addFemaFooter(doc, (doc as any).lastAutoTable.finalY);
    doc.save(`FEMA-009-0-123-ForceLaborSummary-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-123 generated', color: 'var(--color-success)' });
  };

  // 009-0-124 Materials Summary Record (FF-104-FY-21-138)
  // Columns per FEMA form: Vendor Name | Description of Material | Unit of Measure | Quantity | Unit Price | Total Price | Date Purchased | Date Used | Invoice/Stock No.
  const generate124 = () => {
    const doc = new jsPDF('landscape');
    const y = addFemaHeader(doc, '009-0-124 (FF-104-FY-21-138)', 'Materials Summary Record', headerData);

    const materialRows: string[][] = [];
    invoices.forEach(inv => {
      try {
        const items = JSON.parse(inv.lineItems || '[]');
        items.forEach((item: any) => {
          if (item.description && (item.category === 'materials' || item.description.toLowerCase().includes('material'))) {
            const vendor = contractors.find(c => String(c.id) === inv.contractorId)?.companyName || '-';
            materialRows.push([
              vendor,
              item.description,
              item.unit || 'each',
              String(item.quantity || 1),
              fmt$(item.rate || item.unitPrice || 0),
              fmt$(item.amount || item.totalPrice || 0),
              fmtDate(inv.periodStart),   // Date Purchased
              fmtDate(inv.periodStart),   // Date Used
              inv.invoiceNumber,
            ]);
          }
        });
      } catch { /* skip */ }
    });

    if (materialRows.length === 0) {
      materialRows.push(['', 'No materials costs recorded', '', '', '', '$0.00', '', '', '']);
    }

    const grandTotalMat = materialRows.reduce((s, r) => s + (parseFloat(r[5]?.replace(/[$,]/g, '') || '0') || 0), 0);

    autoTable(doc, {
      startY: y,
      head: [['Vendor Name', 'Description of Material', 'Unit of Measure', 'Quantity', 'Unit Price', 'Total Price', 'Date Purchased', 'Date Used', 'Invoice/Stock No.']],
      body: materialRows,
      foot: [['', '', '', '', 'GRAND TOTAL', fmt$(grandTotalMat), '', '', '']],
      styles: { fontSize: 7, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold', fontSize: 6.5 },
      footStyles: { fillColor: [240, 245, 250], fontStyle: 'bold', textColor: [0, 0, 0] },
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 10, right: 10 },
    });

    addFemaFooter(doc, (doc as any).lastAutoTable.finalY);
    doc.save(`FEMA-009-0-124-MaterialsSummary-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-124 generated', color: 'var(--color-success)' });
  };

  // 009-0-125 Rented Equipment Summary Record (FF-104-FY-21-139)
  // Pixel-perfect layout matching official FEMA form with W/OPR sub-columns
  const generate125 = () => {
    const doc = new jsPDF({ format: 'letter', unit: 'mm' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const L = 10, R = pw - 10, CW = R - L;

    // DHS / FEMA header
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('DEPARTMENT OF HOMELAND SECURITY', pw / 2, 10, { align: 'center' });
    doc.setFontSize(8.5);
    doc.text('Federal Emergency Management Agency', pw / 2, 14.5, { align: 'center' });
    doc.setFontSize(7); doc.setFont('helvetica', 'normal');
    doc.text('OMB Control Number: 1660-0017', R, 10, { align: 'right' });
    doc.text('Expires: September 30, 2027', R, 14.5, { align: 'right' });

    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text('RENTED EQUIPMENT SUMMARY RECORD', pw / 2, 20, { align: 'center' });
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text('FEMA Form 009-0-125 (FF-104-FY-21-139)', pw / 2, 24.5, { align: 'center' });

    // Paperwork Burden box
    let y = 27;
    const boxH = 17;
    doc.setDrawColor(0); doc.setLineWidth(0.3);
    doc.rect(L, y, CW, boxH);
    doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
    doc.text('PAPERWORK BURDEN DISCLOSURE NOTICE', pw / 2, y + 3.5, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8);
    const notice = 'Public reporting burden for this data collection is estimated to average .5 hours per response. The burden estimate includes time for reviewing instructions, searching existing data sources, gathering and maintaining the data needed, and completing and submitting this form. You are not required to respond to this collection of information unless a valid OMB control number is displayed on this form. Send comments regarding the accuracy of the burden estimate and any suggestions for reducing the burden to: Information Collections Management, Department of Homeland Security, FEMA, 500 C Street, SW, Washington, DC 20472-3100, Paperwork Reduction Project (1660-0017). NOTE: Do not send your completed questionnaire to this address.';
    doc.text(doc.splitTextToSize(notice, CW - 4), L + 2, y + 7);

    // Header grid
    y += boxH + 1;
    const hRow = 7;
    const c1 = 58, c2 = 32, c3 = 38, c4 = CW - c1 - c2 - c3;

    const hCell = (x: number, cy: number, w: number, h: number, label: string, value: string) => {
      doc.rect(x, cy, w, h);
      doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
      doc.text(label, x + 1, cy + 2.5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
      doc.text(doc.splitTextToSize(value, w - 2), x + 1, cy + 5.5);
    };

    hCell(L,               y, c1, hRow, 'APPLICANT',  headerData.applicant);
    hCell(L + c1,          y, c2, hRow, 'PA ID NO.',  paId);
    hCell(L + c1 + c2,     y, c3, hRow, 'PROJECT NO.', headerData.projectNumber);
    hCell(L + c1 + c2 + c3, y, c4, hRow, 'DISASTER NO.', headerData.disasterNumber);
    y += hRow;

    hCell(L,              y, c1 + c2, hRow, 'LOCATION/SITE', headerData.location);
    hCell(L + c1 + c2,    y, c3,      hRow, 'CATEGORY',      headerData.category);
    const periodStr = headerData.periodStart ? `${fmtDate(headerData.periodStart)} to ${fmtDate(headerData.periodEnd)}` : '';
    hCell(L + c1 + c2 + c3, y, c4, hRow, 'PERIOD COVERING', periodStr);
    y += hRow;

    const descH = 11;
    doc.rect(L, y, CW, descH);
    doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
    doc.text('DESCRIPTION OF WORK PERFORMED', L + 1, y + 2.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.text(doc.splitTextToSize(workDescription, CW - 2), L + 1, y + 6);
    y += descH;

    // Data table
    // Cols: TYPE OF EQUIPMENT | DATES & HRS | RATE/HR (W/OPR | W/OUT OPR) | TOTAL COST | VENDOR | DATE+AMT PAID | INVOICE NO. | CHECK NO.
    const cols = [43, 22, 17, 18, 18, 27, 21, 15, 11];
    const hdr1H = 6, hdr2H = 5, dataH = 9;

    const drawCell = (x: number, cy: number, w: number, h: number) => doc.rect(x, cy, w, h);

    doc.setFontSize(5.5); doc.setFont('helvetica', 'bold');
    let cx = L;

    drawCell(cx, y, cols[0], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('TYPE OF EQUIPMENT\n(Indicate size, capacity, HP,\nmake and model)', cols[0] - 2), cx + 1, y + 2.5);
    cx += cols[0];

    drawCell(cx, y, cols[1], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('DATES AND\nHOURS USED', cols[1] - 2), cx + 1, y + 3);
    cx += cols[1];

    const rateW = cols[2] + cols[3];
    drawCell(cx, y, rateW, hdr1H);
    doc.text('RATE PER HOUR', cx + rateW / 2, y + 3.5, { align: 'center' });
    const rateX = cx;
    cx += rateW;

    drawCell(cx, y, cols[4], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('TOTAL\nCOST', cols[4] - 2), cx + 1, y + 3);
    cx += cols[4];

    drawCell(cx, y, cols[5], hdr1H + hdr2H);
    doc.text('VENDOR', cx + 1, y + 4);
    cx += cols[5];

    drawCell(cx, y, cols[6], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('DATE AND\nAMOUNT PAID', cols[6] - 2), cx + 1, y + 3);
    cx += cols[6];

    drawCell(cx, y, cols[7], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('INVOICE\nNO.', cols[7] - 2), cx + 1, y + 3);
    cx += cols[7];

    drawCell(cx, y, cols[8], hdr1H + hdr2H);
    doc.text(doc.splitTextToSize('CHECK\nNO.', cols[8] - 2), cx + 1, y + 3);

    y += hdr1H;
    drawCell(rateX, y, cols[2], hdr2H);
    doc.text('W/OPR', rateX + 1, y + 3.2);
    drawCell(rateX + cols[2], y, cols[3], hdr2H);
    doc.text('W/OUT OPR', rateX + cols[2] + 1, y + 3.2);
    y += hdr2H;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    const dataRows = rentedEquip.length > 0
      ? rentedEquip.map(r => [
          r.equipmentName, r.datesUsed,
          r.rateWithOpr ? fmt$(parseFloat(r.rateWithOpr)) : '',
          r.rateWithoutOpr ? fmt$(parseFloat(r.rateWithoutOpr)) : '',
          r.totalCost ? fmt$(parseFloat(r.totalCost)) : '',
          r.vendor, r.dateAmountPaid, r.invoiceNumber, r.checkNumber,
        ])
      : Array(10).fill(Array(9).fill(''));

    dataRows.forEach(row => {
      cx = L;
      cols.forEach((w, i) => {
        drawCell(cx, y, w, dataH);
        if (row[i]) doc.text(String(row[i]), cx + 1, y + 5.5);
        cx += w;
      });
      y += dataH;
    });

    // Grand total row
    const gtH = 7;
    cx = L;
    cols.forEach(w => { drawCell(cx, y, w, gtH); cx += w; });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text('GRAND TOTAL', L + CW / 2, y + 4.5, { align: 'center' });
    const gtTotal = rentedEquip.reduce((s, r) => s + (parseFloat(r.totalCost) || 0), 0);
    if (gtTotal > 0) {
      const totalColX = L + cols[0] + cols[1] + cols[2] + cols[3];
      doc.text(fmt$(gtTotal), totalColX + cols[4] - 1, y + 4.5, { align: 'right' });
    }
    y += gtH + 5;

    // Certification
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    doc.text(
      'I CERTIFY THAT THE ABOVE INFORMATION WAS OBTAINED FROM PAYROLL RECORDS, INVOICES, OR OTHER DOCUMENTS THAT ARE AVAILABLE FOR AUDIT.',
      pw / 2, y, { align: 'center', maxWidth: CW }
    );
    y += 8;
    doc.setLineWidth(0.3);
    const certW = 62, titleW = 88, dateW = CW - certW - titleW;
    [[certW, 'CERTIFIED'], [titleW, 'TITLE'], [dateW, 'DATE']].reduce((x, [w, label]) => {
      doc.rect(L + (x as number), y, w as number, 8);
      doc.setFontSize(5.5); doc.text(label as string, L + (x as number) + 1, y + 2.5);
      return (x as number) + (w as number);
    }, 0);

    // Form number at bottom
    doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(80);
    doc.text('FEMA Form FF-104-FY-21-139 (formerly 009-0-125)', L, ph - 6);
    doc.text('Page 1 of 1', R, ph - 6, { align: 'right' });
    doc.setTextColor(0);

    doc.save(`FEMA-009-0-125-RentedEquipment-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-125 generated', color: 'var(--color-success)' });
  };

  // 009-0-126 Contract Work Summary Record (FF-104-FY-21-140)
  // Columns per FEMA form: Date Worked | Contractor Name | Contract No. | Invoice/Billing No. | Scope of Work | Amount
  const generate126 = () => {
    const doc = new jsPDF();
    const y = addFemaHeader(doc, '009-0-126 (FF-104-FY-21-140)', 'Contract Work Summary Record', headerData);

    const contMap = Object.fromEntries(contractors.map(c => [String(c.id), c.companyName]));
    const rows = invoices.map(inv => {
      const recon = reconciliations.find(r => r.contractorId === inv.contractorId && r.status === 'approved');
      const amount = recon ? recon.approvedAmount : parseFloat(inv.totalAmount) || 0;
      const dateWorked = inv.periodStart
        ? (inv.periodEnd && inv.periodEnd !== inv.periodStart
            ? `${fmtDate(inv.periodStart)} – ${fmtDate(inv.periodEnd)}`
            : fmtDate(inv.periodStart))
        : '';
      return [
        dateWorked,
        contMap[inv.contractorId] || inv.contractorId,
        '',                                         // Contract No. — blank until linked
        inv.invoiceNumber,
        'ROW Debris Collection & Disposal',
        fmt$(amount),
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['Date Worked', 'Contractor Name', 'Contract No.', 'Invoice / Billing No.', 'Scope of Work', 'Amount']],
      body: rows,
      foot: [['', '', '', '', 'GRAND TOTAL', fmt$(contractTotal)]],
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold' },
      footStyles: { fillColor: [240, 245, 250], fontStyle: 'bold', textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: 28 },
        2: { cellWidth: 22 },
        3: { cellWidth: 28 },
        5: { halign: 'right', fontStyle: 'bold', cellWidth: 24 },
      },
      margin: { left: 14, right: 14 },
    });

    addFemaFooter(doc, (doc as any).lastAutoTable.finalY);
    doc.save(`FEMA-009-0-126-ContractWork-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-126 generated', color: 'var(--color-success)' });
  };

  // 009-0-127 Force Account Equipment Summary Record (FF-104-FY-21-141)
  // Columns per FEMA form: Type of Equipment | Specifications / Fleet # | Equipment Code No. | Operator | Date(s) Used | Hours Operated | FEMA Rate ($/hr) | Total Cost
  const generate127 = () => {
    const doc = new jsPDF('landscape');
    const y = addFemaHeader(doc, '009-0-127 (FF-104-FY-21-141)', 'Force Account Equipment Summary Record', headerData);

    autoTable(doc, {
      startY: y,
      head: [['Type of Equipment', 'Specifications / Fleet #', 'Equipment Code No.', 'Operator Name', 'Date(s) Used', 'Hours Operated', 'FEMA Rate ($/hr)', 'Total Cost']],
      body: faEquipment.map(e => [
        e.equipmentType,
        [e.description, e.fleetNumber].filter(Boolean).join(' / ') || e.fleetNumber || '-',
        '',                           // Equipment Code No. — from FEMA rate schedule
        e.operatorName || '-',
        fmtDate(e.dateUsed),
        e.hoursUsed.toFixed(1),
        fmt$(e.hourlyRate),
        fmt$(e.totalCost),
      ]),
      foot: [[
        '', '', '', '', 'TOTALS',
        faEquipment.reduce((s, e) => s + e.hoursUsed, 0).toFixed(1),
        '',
        fmt$(equipTotal),
      ]],
      styles: { fontSize: 7.5, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 41, 59], fontStyle: 'bold', fontSize: 7 },
      footStyles: { fillColor: [240, 245, 250], fontStyle: 'bold', textColor: [0, 0, 0] },
      columnStyles: {
        5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 10, right: 10 },
    });

    addFemaFooter(doc, (doc as any).lastAutoTable.finalY);
    doc.save(`FEMA-009-0-127-ForceEquipment-${disasterNumber}.pdf`);
    notifications.show({ message: 'Form 009-0-127 generated', color: 'var(--color-success)' });
  };

  const generateAll = () => {
    generate49();
    setTimeout(generate123, 300);
    setTimeout(generate124, 600);
    setTimeout(generate125, 900);
    setTimeout(generate126, 1200);
    setTimeout(generate127, 1500);
  };

  // ── Form cards ─────────────────────────────────────────────
  const forms = [
    { id: '009-0-49',  title: 'Summary Record',                   sub: 'Cover sheet — rolls up all cost categories',                                                         gen: generate49,  total: grandTotal,    ready: true },
    { id: '009-0-123', title: 'Force Account Labor Summary',       sub: `${faLabor.length} labor records`,                                                                     gen: generate123, total: laborTotal,    ready: faLabor.length > 0 },
    { id: '009-0-124', title: 'Materials Summary Record',          sub: 'Materials from vendor invoice line items',                                                             gen: generate124, total: 0,             ready: true },
    { id: '009-0-125', title: 'Rented Equipment Summary',          sub: `${rentedEquip.length} rented equipment records`,                                                       gen: generate125, total: rentedEquip.reduce((s, r) => s + (parseFloat(r.totalCost) || 0), 0), ready: true },
    { id: '009-0-126', title: 'Contract Work Summary Record',      sub: `${invoices.length} invoices · ${reconciliations.filter(r => r.status === 'approved').length} approved`, gen: generate126, total: contractTotal, ready: invoices.length > 0 },
    { id: '009-0-127', title: 'Force Account Equipment Summary',   sub: `${faEquipment.length} equipment records`,                                                              gen: generate127, total: equipTotal,    ready: faEquipment.length > 0 },
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3} c="black">FEMA PA Forms</Title>
          <Text size="sm" c="dimmed">Category A — Debris Removal · Forms verified against FF-104-FY-21-xxx series</Text>
        </div>
        <Button leftSection={<IconDownload size={14} />} onClick={generateAll} size="xs">
          Generate All Forms
        </Button>
      </Group>

      {/* Config */}
      <Paper p="sm" radius="md" withBorder>
        <Group gap="md" wrap="wrap">
          <TextInputInline label="Project / PW No." value={projectNumber} onChange={setProjectNumber} w={130} placeholder="Assigned by FEMA" />
          <TextInputInline label="PA ID No." value={paId} onChange={setPaId} w={110} placeholder="e.g. 120-99999" />
          <div>
            <Text size="xs" c="dimmed">Applicant</Text>
            <Text size="sm" fw={500}>{applicantName}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Period</Text>
            <Text size="sm" fw={500}>{fmtDate(period.start)} — {fmtDate(period.end)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Grand Total</Text>
            <Text size="sm" fw={700} c="var(--color-primary-700)">{fmt$(grandTotal)}</Text>
          </div>
        </Group>
      </Paper>

      {/* Pre-export validation */}
      {(() => {
        const warnings: string[] = [];
        if (!projectNumber) warnings.push('Project / PW number not entered — required for FEMA submission.');
        if (!paId) warnings.push('PA ID not entered — required on form headers.');
        const unreconciled = invoices.filter(inv =>
          !reconciliations.some(r => r.status === 'approved' && r.contractorId === inv.contractorId &&
            r.periodStart === inv.periodStart && r.periodEnd === inv.periodEnd)
        );
        if (unreconciled.length > 0) warnings.push(`${unreconciled.length} invoice${unreconciled.length !== 1 ? 's' : ''} not yet reconciled — 009-0-126 will use unverified amounts.`);
        if (invoices.length === 0) warnings.push('No invoices found — 009-0-126 Contract Work Summary will be empty.');
        if (warnings.length === 0) return null;
        return (
          <Paper p="sm" radius="md" withBorder style={{ borderLeft: '3px solid #C68700', background: '#fffbeb' }}>
            <Text size="xs" fw={700} c="#885604" mb={4} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pre-Export Checklist
            </Text>
            {warnings.map((w, i) => (
              <Text key={i} size="xs" c="#885604">⚠ {w}</Text>
            ))}
          </Paper>
        );
      })()}

      {/* Form cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {forms.map(f => (
          <Paper key={f.id} p="md" radius="md" withBorder
            style={{ borderTop: `3px solid ${f.ready ? 'var(--color-primary-700)' : '#94a3b8'}`, cursor: 'pointer' }}
            onClick={f.gen}>
            <Group justify="space-between" mb={4}>
              <Badge size="sm" variant="light" color={f.ready ? 'var(--color-primary-700)' : '#94a3b8'}>{f.id}</Badge>
              <IconFileTypePdf size={16} color={f.ready ? 'var(--color-primary-700)' : '#94a3b8'} />
            </Group>
            <Text fw={700} size="sm">{f.title}</Text>
            <Text size="xs" c="dimmed" mb={6}>{f.sub}</Text>
            <Text size="lg" fw={800} c={f.total > 0 ? 'var(--color-primary-700)' : '#94a3b8'}>
              {fmt$(f.total)}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Data summary */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} size="sm" mb="sm">Data Sources</Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <DataStat label="Force Account Labor" value={String(faLabor.length)} sub={`${fmt$(laborTotal)} total cost`} />
          <DataStat label="Force Account Equipment" value={String(faEquipment.length)} sub={`${fmt$(equipTotal)} total cost`} />
          <DataStat label="Contract Invoices" value={String(invoices.length)} sub={`${fmt$(contractTotal)} approved`} />
          <DataStat label="Rented Equipment" value={String(rentedEquip.length)} sub="records" />
          <DataStat label="Trucks Certified" value={String(trucks.length)} sub="truck certifications" />
          <DataStat label="Contractors" value={String(contractors.length)} sub="registered contractors" />
          <DataStat label="Reconciliations" value={String(reconciliations.filter(r => r.status === 'approved').length)} sub="approved" />
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}

// ── Tiny inline text input ──────────────────────────────────
function TextInputInline({ label, value, onChange, w, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; w?: number; placeholder?: string;
}) {
  return (
    <div style={{ width: w }}>
      <Text size="xs" c="dimmed">{label}</Text>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px', fontSize: 13 }} />
    </div>
  );
}

function DataStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700}>{value}</Text>
      <Text size="xs" c="dimmed">{sub}</Text>
    </div>
  );
}
